import RocketlaneLogo from 'module/ui/assets/rocketlane_logo_64px.png';
import { Menu as DropdownMenu } from 'module/ui/components/atoms/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Layout } from 'module/ui/components/atoms/layout';
import { faGear, faSignOut } from '@fortawesome/pro-solid-svg-icons';
import {
  faUser,
  faBuildings,
  // faHome,
  faPenRuler,
  faSignalBars,
} from '@fortawesome/pro-solid-svg-icons';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { Link, useLocation, useNavigate } from '@rocketlane/rocket-router';
import { Dropdown, Image, Menu, Tooltip } from 'antd';
import { useAccountInitialDataQuery } from 'queries/account';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled, { css } from 'styled-components';

export const RouterShellComponent = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  align-content: stretch;
  max-height: 100vh;
  min-height: 100vh;
  height: 100vh;
  background: var(--scarlet-gray-80);
  overflow: hidden;
`;
const Sidebar = styled(Layout.Sider)<{ $hideSidebar?: boolean }>`
  overflow: auto;
  padding: 0;
  /* Inline style overrides, so !important is unavoidable */
  width: var(--side-nav-width) !important;
  min-width: var(--side-nav-width) !important;
  max-width: var(--side-nav-width) !important;
  flex: 0 0 var(--side-nav-width) !important;

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-06) 0;
    background-color: var(--scarlet-gray-80);
  }

  .ant-layout-sider-trigger {
    display: none;
  }
  background: var(--side-nav-bg);
  /* added so that when notifications drawer opens, it opens from beneath the left nav */
  z-index: var(--zindex-modal);
`;
const Logo = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 24px;
  .ant-image-img {
    border-radius: var(--border-radius-base);
  }
`;

export const Content = styled(Layout.Content)<{
  $gray?: boolean;
  isAgentBuilderPath?: boolean;
}>`
  /* background-color: var(--page-background); */
  margin: 8px 8px 8px 0px;
  margin-left: ${({ isAgentBuilderPath }) =>
    isAgentBuilderPath ? '8px' : '0px'};
  width: calc(100% - 56px);
  height: calc(100vh - 16px);
  border-radius: var(--border-radius-small);
  ${({ $gray = false }) =>
    $gray
      ? css`
          background-color: var(--scarlet-gray-80);
        `
      : ''};
`;
const StyledLink = styled(Link)``;
const MenuIconStyles = css`
  color: var(--icon-primary);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-xsmall);
`;
const MenuIconHoverStyles = css`
  background-color: var(--background-secondary);
`;
const MenuItem = styled(Menu.Item)`
  & ${StyledLink} {
    ${MenuIconStyles};
  }
`;
const StyledMenu = styled(Menu)`
  background: inherit;
  flex: 1;
  margin-top: var(--spacing-06);
  &.ant-menu-vertical {
    border-right: none;
    .ant-menu-submenu-title {
      padding-right: unset;
      &:active {
        color: inherit;
        background-color: inherit;
      }
    }
  }
  .ant-menu-item {
    height: 40px;
    width: 48px;
    margin: 0;
    padding: 0;
    .ant-menu-title-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
    &:hover {
      ${StyledLink} {
        ${MenuIconHoverStyles};
      }
    }
  }
  ${MenuItem}.ant-menu-item-selected {
    background-color: transparent;
    ${StyledLink} {
      background-color: var(--background-primary);
      box-shadow: var(--shadow-02);
    }
  }
  .ant-menu-submenu {
    color: var(--side-nav-text);
    &.ant-menu-submenu-open {
      color: var(--side-nav-text);
    }
  }
  .ant-menu-submenu-popup {
    &.ant-menu {
      background-color: var(--gray-10);
    }
  }
  .ant-menu-submenu-arrow {
    display: none;
  }
  .ant-menu-item {
    &:active {
      color: inherit;
      background-color: inherit;
    }
  }
`;
const NavActionContent = styled.div`
  ${MenuIconStyles};
`;

const NavActionWrapper = styled.div`
  width: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 40px;
  cursor: pointer;
  &:hover,
  &.ant-dropdown-open {
    ${NavActionContent} {
      ${MenuIconHoverStyles};
    }
  }
`;

const MENU_KEYS = {
  HOME: 'home',
  ACCOUNTS: 'accounts',
  MEETING_TEMPLATES: 'meeting-templates',
  ALL_SIGNALS: 'all-signals',
  SIGNALS_V2: 'signals-v2',
  TEMPLATES: 'templates',
  AGENTS: 'agents',
  AGENT_BUILDER: 'agent-builder',
};

const findActiveKey = (pathname: string) => {
  if (pathname.includes(ROUTE_PATHS.ACCOUNTS)) {
    return MENU_KEYS.ACCOUNTS;
  }
  if (pathname.includes(ROUTE_PATHS.TEMPLATES)) {
    return MENU_KEYS.TEMPLATES;
  }
  if (pathname.includes(ROUTE_PATHS.ALL_SIGNALS)) {
    return MENU_KEYS.ALL_SIGNALS;
  }
  if (pathname.includes(ROUTE_PATHS.SIGNALS_V2)) {
    return MENU_KEYS.SIGNALS_V2;
  }

  if (pathname.includes(ROUTE_PATHS.AGENT_BUILDER)) {
    return MENU_KEYS.AGENT_BUILDER;
  }

  if (pathname.includes(ROUTE_PATHS.AGENTS)) {
    return MENU_KEYS.AGENTS;
  }

  return MENU_KEYS.HOME;
};

export const RouterShell = ({ children }: { children: React.ReactNode }) => {
  const { data } = useAccountInitialDataQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const onLogout = useCallback(() => navigate('/logout'), [navigate]);

  // Check if current path is agent-builder or document-agent to hide sidebar
  const pathname = location.pathname;
  const agentBuilderSubPaths = [
    '/create',
    '/edit',
    '/livepreview',
    '/experts/',
    '/migration-agents/',
    '/documentation/test-agent',
    '/documentation/preview',
    '/documentation/',
  ];
  const excludedAgentBuilderSubPaths = ['/migration-agents/create'];
  const isAgentBuilderPath =
    pathname.startsWith('/agent-builder') &&
    agentBuilderSubPaths.some((subPath) => pathname.includes(subPath)) &&
    !excludedAgentBuilderSubPaths.some((subPath) => pathname.includes(subPath));

  return (
    <RouterShellComponent>
      {!isAgentBuilderPath && (
        <Sidebar>
          <Link to={ROUTE_PATHS.HOME}>
            <Logo>
              <Image
                width={24}
                height={24}
                preview={false}
                src={
                  data?.accountMeta?.company?.companyLogoUrl || RocketlaneLogo
                }
                title="logo"
                alt="logo"
              />
            </Logo>
          </Link>
          <StyledMenu
            mode="vertical"
            selectedKeys={[findActiveKey(location.pathname)]}
          >
            {/* <MenuItem key={MENU_KEYS.HOME}>
            <StyledLink to={ROUTE_PATHS.HOME}>
              <FontAwesomeIcon icon={faHome} />
            </StyledLink>
          </MenuItem> */}
            <MenuItem key={MENU_KEYS.ACCOUNTS}>
              <Tooltip
                title={<FormattedMessage id="accounts.page.title" />}
                placement="right"
              >
                <StyledLink to={ROUTE_PATHS.ACCOUNTS}>
                  <FontAwesomeIcon icon={faBuildings} size={12} />
                </StyledLink>
              </Tooltip>
            </MenuItem>
            <MenuItem key={MENU_KEYS.ALL_SIGNALS}>
              <Tooltip
                title={
                  <FormattedMessage id="accounts.page.all_signals.title" />
                }
                placement="right"
              >
                <StyledLink to={ROUTE_PATHS.ALL_SIGNALS}>
                  <FontAwesomeIcon icon={faSignalBars} size={12} />
                </StyledLink>
              </Tooltip>
            </MenuItem>
            {/* <MenuItem key={MENU_KEYS.SIGNALS_V2}>
              <Tooltip
                title={<FormattedMessage id="accounts.page.signals_v2.title" />}
                placement="right"
              >
                <StyledLink to={ROUTE_PATHS.SIGNALS_V2}>
                  <FontAwesomeIcon icon={faSignalBars} size={12} />
                </StyledLink>
              </Tooltip>
            </MenuItem> */}
            <MenuItem key={MENU_KEYS.AGENT_BUILDER}>
              <Tooltip
                title={
                  <FormattedMessage id="accounts.page.agent_builder.title" />
                }
                placement="right"
              >
                <StyledLink to={`${ROUTE_PATHS.AGENT_BUILDER}`}>
                  <FontAwesomeIcon icon={faSparkles} size={12} />
                </StyledLink>
              </Tooltip>
            </MenuItem>
            <MenuItem key={MENU_KEYS.TEMPLATES}>
              <Tooltip
                title={<FormattedMessage id="accounts.page.templates.title" />}
                placement="right"
              >
                <StyledLink
                  to={`${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.MEETING_PAGE_TEMPLATE_LIST}`}
                >
                  <FontAwesomeIcon icon={faPenRuler} size={12} />
                </StyledLink>
              </Tooltip>
            </MenuItem>
          </StyledMenu>
          {/* TODO: replace this with popover */}

          <Dropdown
            placement={'rightTop' as any}
            overlay={
              <DropdownMenu>
                <DropdownMenu.Item
                  icon={<FontAwesomeIcon icon={faGear} size={12} />}
                >
                  <Link
                    to={`${ROUTE_PATHS.SETTINGS}/${ROUTE_PATHS.SETTINGS_SIGNAL_CONFIGURATION_LIST}`}
                  >
                    <FormattedMessage id="settings" />
                  </Link>
                </DropdownMenu.Item>
                <DropdownMenu.Divider />
                <DropdownMenu.Item
                  onClick={onLogout}
                  icon={<FontAwesomeIcon icon={faSignOut} size={12} />}
                >
                  <FormattedMessage id="logout" />
                </DropdownMenu.Item>
              </DropdownMenu>
            }
          >
            <NavActionWrapper>
              <NavActionContent>
                <FontAwesomeIcon icon={faUser} size={12} />
              </NavActionContent>
            </NavActionWrapper>
          </Dropdown>
        </Sidebar>
      )}
      <Content $gray={true} isAgentBuilderPath={isAgentBuilderPath}>
        {children}
      </Content>
    </RouterShellComponent>
  );
};
